import React, { useEffect, useState } from "react";
import { Testimonials } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import LivePreviewPane from "@/components/admin/LivePreviewPane";
import TestimonialPreview from "@/components/admin/TestimonialPreview";
import { notifyPreviewRefresh } from "@/components/admin/GlobalPreviewPane";

const empty = { author: "", role: "", quote: "", rating: 5, is_active: true, sort_order: 0 };

const TestimonialsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await Testimonials.list()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit("new"); setForm(empty); };
  const openEdit = (t) => { setEdit(t.id); setForm({ ...empty, ...t }); };
  const close = () => { setEdit(null); setForm(empty); };

  const save = async ({ keepOpen = false } = {}) => {
    if (!form.author || !form.quote) { toast.error("Author and quote required"); return; }
    setSaving(true);
    try {
      let saved;
      if (edit === "new") saved = await Testimonials.create(form);
      else saved = await Testimonials.update(edit, form);
      toast.success("Saved");
      notifyPreviewRefresh();
      if (keepOpen) {
        if (saved && saved.id) {
          setEdit(saved.id);
          setForm({ ...empty, ...saved });
        }
        load();
      } else {
        close();
        load();
      }
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete testimonial from ${t.author}?`)) return;
    try { await Testimonials.remove(t.id); toast.success("Deleted"); notifyPreviewRefresh(); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-10" data-testid="testimonials-manager">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Social proof</div>
          <h1 className="font-display font-black text-4xl text-white">Testimonials</h1>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold" data-testid="new-testimonial-button">
          <Plus className="h-4 w-4" /> New Testimonial
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="glass rounded-xl p-10 text-center text-slate-400">No testimonials yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {items.map((t) => (
            <div key={t.id} className="glass rounded-2xl p-6" data-testid={`testimonial-card-${t.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-display font-bold text-white">{t.author}</div>
                  <div className="text-xs text-slate-400 mt-1">{t.role}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(t)} className="text-slate-300 hover:text-white" data-testid={`edit-testimonial-${t.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(t)} className="text-slate-300 hover:text-rose-400" data-testid={`delete-testimonial-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="flex items-center gap-1 mt-3">
                {Array.from({ length: t.rating || 5 }).map((_, i) => (
                  <Star key={i} className="h-3 w-3 text-atc-green" style={{ fill: "#3FBF3F" }} />
                ))}
              </div>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed line-clamp-4">"{t.quote}"</p>
              <div className="mt-4 text-xs">
                <span className={`px-2 py-0.5 rounded-full uppercase tracking-wider ${t.is_active ? "bg-atc-green/15 text-atc-green" : "bg-slate-700/30 text-slate-400"}`}>
                  {t.is_active ? "Live" : "Hidden"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-slate-950 border-white/10 text-white max-w-[1200px] w-[95vw] max-h-[92vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/5 shrink-0">
            <DialogTitle>{edit === "new" ? "New testimonial" : "Edit testimonial"}</DialogTitle>
          </DialogHeader>
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-5 px-6 py-5 overflow-y-auto flex-1">
          <div className="space-y-4 min-w-0">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Author</label>
                <Input value={form.author} onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))} className="bg-slate-950 border-white/10 mt-1" data-testid="t-author" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Role / location</label>
                <Input value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className="bg-slate-950 border-white/10 mt-1" data-testid="t-role" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Quote</label>
              <Textarea value={form.quote} onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))} rows={4} className="bg-slate-950 border-white/10 mt-1" data-testid="t-quote" />
            </div>
            <div className="grid sm:grid-cols-3 gap-3 items-end">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Rating (1-5)</label>
                <Input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm((f) => ({ ...f, rating: Number(e.target.value) }))} className="bg-slate-950 border-white/10 mt-1" data-testid="t-rating" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Sort</label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))} className="bg-slate-950 border-white/10 mt-1" data-testid="t-sort" />
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="t-active" />
                <span className="text-sm text-slate-300">Active</span>
              </div>
            </div>
          </div>

          <div className="hidden lg:block sticky top-0 self-start h-[calc(92vh-9rem)]">
            <LivePreviewPane title="Live preview · Homepage card" testId="testimonial-live-preview">
              <TestimonialPreview t={form} />
            </LivePreviewPane>
          </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-white/5 shrink-0">
            <button onClick={close} className="px-4 py-2 rounded-full text-sm text-slate-300 hover:text-white">Cancel</button>
            <button onClick={() => save({ keepOpen: true })} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium text-slate-200 border border-white/15 hover:bg-slate-900/50 disabled:opacity-60" data-testid="t-save-keep">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save & keep editing
            </button>
            <button onClick={() => save()} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full brand-gradient text-white text-sm font-semibold disabled:opacity-60" data-testid="t-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save & close
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestimonialsManager;
